import React, { type ComponentType } from "react";
import { Body } from "@/sprout/typography";
import useTranslation from "@hooks/use_translation";
import { useTaskDismiss } from "./taskActions";
import "./task_card.scss";
import { type Task } from "@/types/task";
import type { IconProps } from "@/sprout/icons/base";
import GreenhouseLogoIcon from "@/sprout/icons/greenhouse_logo";
import { SecondaryButtonLink, TertiaryButton } from "@sprout/buttons";
import useTaskStatusText from "@hooks/use_task_status_text";

type TaskCardProps = {
  task: Task;
  title: string;
  description?: string;
  body?: JSX.Element;
  actionText: string;
  icon?: ComponentType<IconProps>;
  theme?: {
    colors: {
      background?: string;
      border?: string;
      iconBackground?: string;
    };
  };
  dismissable?: boolean;
  externalLink?: boolean;
  helpText?: string;
  className?: string;
  redirectTo?: string;
  taskableType?: string;
} & (
  | { actionHref?: string | null; Modal?: never }
  | { actionHref?: never; Modal: React.ComponentType }
);

const defaultTheme = {
  colors: { background: "bg-pale-blue", border: "border-iris-e50", iconBackground: "bg-iris-e50" },
};

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  title,
  description,
  body,
  actionHref,
  Modal,
  actionText,
  icon: Icon = GreenhouseLogoIcon,
  theme = defaultTheme,
  externalLink = true,
  dismissable = true,
  helpText,
  className,
  redirectTo,
  taskableType,
}) => {
  const { t } = useTranslation("tasks");
  const handleDismiss = useTaskDismiss(task.id, { redirectTo, taskableType });
  const statusText = useTaskStatusText(task);

  return (
    <div
      className={`flex flex-col justify-between task-card w-[295px] p-4 border rounded-lg ${theme.colors.background} ${theme.colors.border} ${className}`}
    >
      <div>
        <div className="flex gap-[4px] items-center mb-[4px]">
          <div
            className={`task-card__icon flex items-center justify-center ${theme.colors.iconBackground}`}
          >
            <Icon size="sm" />
          </div>
          <Body className="!text-xs !ml-1" metadata>
            {statusText}
          </Body>
        </div>
        <Body medium>{title}</Body>
        {(body && body) || <Body className="!text-sm">{description}</Body>}
      </div>
      <div className="mt-4">{helpText && <Body secondary>{helpText}</Body>}</div>
      <div className="flex justify-start items-center space-x-4">
        {actionHref && (
          <SecondaryButtonLink href={actionHref} size="medium" external={externalLink}>
            {actionText}
          </SecondaryButtonLink>
        )}
        {Modal && <Modal />}
        {dismissable && (
          <TertiaryButton secondary onClick={handleDismiss}>
            {t("common.dismiss")}
          </TertiaryButton>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
